// Ids are minted by the MCP service as url-safe tokens; the backend caps them at 128 characters.
const REQUEST_ID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;
const LOCAL_HOSTS = ['localhost', '127.0.0.1'];
const DENIED_ERROR = 'access_denied';

export class InvalidMcpAuthorization extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidMcpAuthorization';
    Object.setPrototypeOf(this, InvalidMcpAuthorization.prototype);
  }
}

const LINK_MESSAGE = 'This sign-in link is not valid. Start the connection again from the app.';
const CONFIGURATION_MESSAGE = 'App sign-in is not configured for this environment.';

const tryParseUrl = (value: string): URL | undefined => {
  try {
    return new URL(value);
  } catch {
    return undefined;
  }
};

// The callback comes from build configuration, never from the link, so a crafted link
// cannot send a grant to another host.
const parseCallback = (callbackUrl: string): URL => {
  const url = tryParseUrl(callbackUrl);
  const secure = url?.protocol === 'https:';
  const local = url?.protocol === 'http:' && LOCAL_HOSTS.includes(url.hostname);

  if (!url || !(secure || local) || url.username || url.password) {
    throw new InvalidMcpAuthorization(CONFIGURATION_MESSAGE);
  }

  return url;
};

const parseRequestId = (search: string): string => {
  const ids = new URLSearchParams(search).getAll('request');

  if (ids.length !== 1 || !REQUEST_ID_PATTERN.test(ids[0])) {
    throw new InvalidMcpAuthorization(LINK_MESSAGE);
  }

  return ids[0];
};

export class McpAuthorization {
  readonly requestId: string;

  private readonly callback: URL;

  constructor(callbackUrl: string, search: string) {
    this.callback = parseCallback(callbackUrl);
    this.requestId = parseRequestId(search);
  }

  grantedUrl(grant: string): string {
    return this.callbackWith({ grant, request: this.requestId });
  }

  deniedUrl(): string {
    return this.callbackWith({ error: DENIED_ERROR, request: this.requestId });
  }

  private callbackWith(parameters: Record<string, string>): string {
    const url = new URL(this.callback.toString());

    Object.entries(parameters).forEach(([name, value]) => url.searchParams.set(name, value));

    return url.toString();
  }
}
